import { Controller } from "@hotwired/stimulus"
import { showToast } from "../lib/toast"
import { track } from "../lib/mixpanel"

// Timesheets → Codes & lists (Kloepfer K7-10, 2026-10-07; K6-8, 2026-10-09).
// The lists behind the time clock, managed from the office: the phone's own
// lists - equipment first, then cost codes: what crews pick from - and,
// office only, Interac's jobs and their phases. Add, edit, archive, restore
// (never delete: old time entries point at them), and Smart Import, where
// any file goes to the AI (the Jobs & Phases sheet is read by its headers)
// and comes back as a preview to check before anything is added - possible
// duplicates first, each marked Duplicate (skipped, the default) or Not a
// duplicate (added), one by one or all at once. Lists come from
// /timesheets/lists and every change answers with them again, so the pop-up
// never reloads the page; a job's phases come from /timesheets/lists/phases.
// Equipment has three columns (K8-27): unit number, type and a description
// that tells two units of one type apart. Smart Import can also replace a
// whole phone list (K8-28): the file becomes the list, and what it doesn't
// have is archived - never deleted. The Mapping and Export sections on the
// left belong to time_mapping_controller.js, and Rates (K13-26) to
// time_rates_controller.js; this one only shows their panes.
// A word search sits on top of every list (K8-22): every word typed has to be
// in the row - its number, name, description or the fields beside it - with
// accents and capitals ignored.
const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c])

const fold = (s) => String(s ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()

const CHIPS = {
  new: "bg-green-100 text-green-700",
  possible: "bg-amber-100 text-amber-700",
  exists: "bg-gray-100 text-gray-600",
  repeat: "bg-gray-100 text-gray-600",
  invalid: "bg-red-50 text-red-600"
}
// Possible duplicates first (they need a decision), then the new ones.
const ORDER = { possible: 0, new: 1, exists: 2, repeat: 3, invalid: 4 }
const DUP_ON = "bg-gray-900 text-white"
const DUP_OFF = "bg-white text-gray-600 hover:bg-gray-50"
// The open item is tinted in the brand colour (Phelipe 2026-10-09: the dark pill was tried; he prefers the brand highlight).
const NAV_ON = ["bg-brand/10", "text-brand", "font-semibold"]
const NAV_OFF = ["text-gray-600", "hover:bg-gray-50"]
const LISTS = ["equipment", "cost_codes", "equipment_types", "materials", "jobs", "labor_types"]
// The foreman's report's fields (K8-34), edited on the row after it's added: a
// unit's type (picked from the Equipment types list) and Interac number, a
// type's Interac category, a material's unit. Their rates live on the Rates
// page now, by year (K13-26).
const EXTRA = {
  equipment: [{ field: "type_id", select: "equipment_types" }, { field: "accounting_number" }],
  equipment_types: [{ field: "category_code" }],
  materials: [{ field: "unit" }]
}

export default class extends Controller {
  static targets = ["modal", "tab", "count", "dropzone", "dropLabel", "fileInput", "error", "preview", "newKey", "newName", "newDesc", "list",
    "replaceModal", "replaceText", "listPane", "mapPane", "ratesPane", "dbPane", "paneTitle", "paneHelp", "jobPicker", "jobSelect", "settingsBox", "search", "searchCount",
    "addModal", "addTitle", "addJob", "newKeyLabel", "newNameLabel", "newDescRow", "addError"]
  static values = { url: String, text: Object, settingsUrl: String, inline: Boolean }

  connect() {
    this.kind = "equipment"
    this.section = null
    this.lists = { equipment: [], cost_codes: [], jobs: [], labor_types: [] }
    this.phases = {} // a job's phases, by job id, once fetched
    this.phasesTotal = 0
    this.jobId = null
    this.showArchived = false
    this.editing = null
    this.query = "" // the word search on top of the list (K8-22)
    this.settings = null // the company's equipment switches, from the lists payload
    this._onKey = (e) => {
      if (e.key !== "Escape") return
      if (this._addOpen) return this.closeAdd() // the Add pop-up first, then the screen
      if (this.hasModalTarget && !this.modalTarget.classList.contains("hidden")) this.close()
    }
    document.addEventListener("keydown", this._onKey)
    // Inline (Company Settings → Timesheets → Codes & Lists): always open.
    if (this.inlineValue) this.open()
    // …#rates (the Foreman's Report's link): straight to the Rates page.
    if (window.location.hash === "#rates") this.tabTargets.find((t) => t.dataset.section === "rates")?.click()
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKey)
  }

  t(key, vars = {}) {
    let s = key.split(".").reduce((o, k) => o?.[k], this.textValue) ?? ""
    for (const [k, v] of Object.entries(vars)) s = s.replace(`%{${k}}`, v)
    return s
  }

  // Equipment carries a description; the other lists don't.
  get withDesc() {
    return this.kind === "equipment"
  }

  // Phases live under a job and are fetched per job.
  get isPhases() {
    return this.kind === "phases"
  }

  get items() {
    return this.isPhases ? (this.phases[this.jobId] || []) : (this.lists[this.kind] || [])
  }

  async open() {
    if (!this.hasModalTarget) return
    this.modalTarget.classList.remove("hidden")
    this.editing = null
    this.section = null
    this._clearPreview()
    this._error(null)
    this._showList(true)
    this._renderTabs()
    this.listTarget.innerHTML = `<p class="text-[13px] text-gray-400">${esc(this.t("loading"))}</p>`
    const data = await this._request("GET", this.urlValue)
    if (data) this._take(data)
  }

  close() {
    if (this._busy || this.inlineValue) return
    this.modalTarget.classList.add("hidden")
  }

  stopPropagation(e) {
    e.stopPropagation()
  }

  switchTab(e) {
    this.kind = e.currentTarget.dataset.kind
    this.section = null
    this.showArchived = false
    this.editing = null
    this._resetSearch()
    this._clearPreview()
    this._error(null)
    this._showList(true)
    this._renderTabs()
    this._renderList()
    if (this.isPhases) this._ensurePhases()
  }

  // Mapping and Export: time_mapping_controller.js draws them in the other pane.
  showSection(e) {
    this.section = e.currentTarget.dataset.section
    this.editing = null
    this._clearPreview()
    this._error(null)
    this._showList(false)
    this._renderTabs()
  }

  _showList(on) {
    const rates = !on && this.section === "rates"
    // Davis-Bacon (K9-21): time_davis_bacon_controller.js draws its own pane.
    const db = !on && this.section === "davis_bacon"
    if (this.hasListPaneTarget) this.listPaneTarget.hidden = !on
    if (this.hasMapPaneTarget) this.mapPaneTarget.hidden = on || rates || db
    if (this.hasRatesPaneTarget) this.ratesPaneTarget.hidden = !rates
    if (this.hasDbPaneTarget) this.dbPaneTarget.hidden = !db
  }

  // ── Phases: which job ──────────────────────────────────────────────

  async _ensurePhases() {
    const jobs = this.lists.jobs.filter((j) => j.active)
    if (!jobs.length) {
      this.jobId = null
      this._renderJobPicker()
      return this._renderList()
    }
    if (!jobs.some((j) => j.id === this.jobId)) this.jobId = jobs[0].id
    this._renderJobPicker()
    if (this.phases[this.jobId]) return this._renderList()
    const jobId = this.jobId
    const data = await this._request("GET", `${this.urlValue}/phases?job_id=${jobId}`)
    if (data?.phases && Number(data.job_id) === jobId) {
      this.phases[jobId] = data.phases
      if (this.isPhases && this.jobId === jobId) this._renderList()
    }
  }

  pickJob(e) {
    this.jobId = Number(e.currentTarget.value) || null
    this.editing = null
    this._resetSearch()
    this._clearPreview()
    this._ensurePhases()
  }

  _renderJobPicker() {
    if (!this.hasJobPickerTarget) return
    this.jobPickerTarget.hidden = !this.isPhases
    if (!this.isPhases || !this.hasJobSelectTarget) return
    const jobs = this.lists.jobs.filter((j) => j.active)
    this.jobSelectTarget.innerHTML = jobs.map((j) => `<option value="${j.id}" ${j.id === this.jobId ? "selected" : ""}>${esc(j.label)}</option>`).join("")
  }

  // ── Word search (K8-22) ────────────────────────────────────────────

  search(e) {
    this.query = e.currentTarget.value
    this._renderList()
  }

  // Escape empties the search first; only an empty search lets Escape close the pop-up.
  clearSearch(e) {
    if (!this.query) return
    e.stopPropagation()
    this._resetSearch()
    this._renderList()
  }

  _resetSearch() {
    this.query = ""
    if (this.hasSearchTarget) this.searchTarget.value = ""
  }

  get _words() {
    return fold(this.query).split(/\s+/).filter(Boolean)
  }

  // Everything a row shows: its number, name, description and the fields beside it.
  _haystack(item) {
    const extra = (EXTRA[this.kind] || []).map((f) => f.select ? (this.lists[f.select] || []).find((t) => t.id === item[f.field])?.label : item[f.field])
    return fold([item.key, item.name, item.description, item.label, ...extra].filter((v) => v !== null && v !== undefined).join(" "))
  }

  // ── By hand ────────────────────────────────────────────────────────
  // Add sits beside the search and opens a pop-up (Phelipe 2026-10-10: "to
  // clean up"): the number, the name and, for equipment, the description.

  get _addOpen() {
    return this.hasAddModalTarget && !this.addModalTarget.classList.contains("hidden")
  }

  openAdd() {
    if (this.isPhases && !this.jobId) return this._error(this.t("errors.job_required"))
    this._error(null)
    this.newKeyTarget.value = ""
    this.newNameTarget.value = ""
    this.newDescTarget.value = ""
    if (this.hasAddTitleTarget) this.addTitleTarget.textContent = this.t("add_title", { list: this.t(`tab_${this.kind}`) })
    if (this.hasAddJobTarget) {
      const job = this.isPhases ? this.lists.jobs.find((j) => j.id === this.jobId) : null
      this.addJobTarget.textContent = job ? `${this.t("job")}: ${job.label}` : ""
      this.addJobTarget.hidden = !job
    }
    this.addModalTarget.classList.remove("hidden")
    this.newKeyTarget.focus()
  }

  closeAdd(e) {
    e?.stopPropagation() // a click on its backdrop must not reach the screen's own backdrop
    if (!this.hasAddModalTarget) return
    this.addModalTarget.classList.add("hidden")
    if (this.hasAddErrorTarget) {
      this.addErrorTarget.textContent = ""
      this.addErrorTarget.classList.add("hidden")
    }
  }

  async add() {
    const key = this.newKeyTarget.value.trim()
    if (!key) {
      this._error(this.t("errors.key_required"))
      this.newKeyTarget.focus()
      return
    }
    if (this.isPhases && !this.jobId) return this._error(this.t("errors.job_required"))
    const body = { key, name: this.newNameTarget.value.trim() }
    if (this.withDesc) body.description = this.newDescTarget.value.trim()
    if (this.isPhases) body.job_id = this.jobId
    const data = await this._request("POST", `${this.urlValue}/${this.kind}`, body)
    if (!data) return
    this.newKeyTarget.value = ""
    this.newNameTarget.value = ""
    this.newDescTarget.value = ""
    this._error(null)
    if (this._addOpen) this.closeAdd()
    else this.newKeyTarget.focus()
    this._take(data)
    showToast(this.t("added_one"))
  }

  // Edit / Save / Cancel / Archive / Restore on a row, and the archived toggle.
  async rowAction(e) {
    const { act, id } = e.currentTarget.dataset
    if (act === "toggleArchived") {
      this.showArchived = !this.showArchived
      return this._renderList()
    }
    if (act === "edit") {
      this.editing = Number(id)
      this._renderList()
      return this.listTarget.querySelector("[data-edit-key]")?.focus()
    }
    if (act === "cancel") {
      this.editing = null
      return this._renderList()
    }
    let body
    if (act === "save") {
      const row = e.currentTarget.closest("[data-row]")
      body = { key: row.querySelector("[data-edit-key]").value.trim(), name: row.querySelector("[data-edit-name]").value.trim() }
      if (this.withDesc) body.description = row.querySelector("[data-edit-desc]")?.value.trim() || ""
      row.querySelectorAll("[data-edit-field]").forEach((el) => { body[el.dataset.editField] = el.value.trim() })
      if (!body.key) return this._error(this.t("errors.key_required"))
    } else {
      body = { active: act === "restore" }
    }
    const data = await this._request("PATCH", `${this.urlValue}/${this.kind}/${id}`, body)
    if (!data) return
    this.editing = null
    this._error(null)
    this._take(data)
    showToast(this.t(act === "save" ? "saved" : act === "restore" ? "restored_toast" : "archived_toast"))
  }

  editKey(e) {
    if (e.key === "Enter") e.currentTarget.closest("[data-row]")?.querySelector("[data-act='save']")?.click()
    if (e.key === "Escape") {
      e.stopPropagation()
      this.editing = null
      this._renderList()
    }
  }

  // ── Smart Import ───────────────────────────────────────────────────

  pickFile(e) {
    if (this._busy) return
    if (e.type === "keydown") e.preventDefault()
    this.fileInputTarget.click()
  }

  fileChosen() {
    const file = this.fileInputTarget.files[0]
    this.fileInputTarget.value = ""
    if (file) this._preview(file)
  }

  dragOver(e) {
    e.preventDefault()
    this.dropzoneTarget.classList.add("border-purple-400", "bg-purple-100")
  }

  dragLeave() {
    this.dropzoneTarget.classList.remove("border-purple-400", "bg-purple-100")
  }

  drop(e) {
    e.preventDefault()
    this.dragLeave()
    const file = e.dataTransfer?.files?.[0]
    if (file && !this._busy) this._preview(file)
  }

  async _preview(file) {
    this._error(null)
    this._clearPreview()
    this._busy = true
    this.dropLabelTarget.textContent = this.t("reading")
    const form = new FormData()
    form.append("file", file)
    const kind = this.kind
    const data = await this._request("POST", `${this.urlValue}/${kind}/preview`, form)
    this._busy = false
    this.dropLabelTarget.textContent = this.t("import_drop")
    if (!data || kind !== this.kind) return
    data.replace = false
    this._previewData = data
    this._renderPreview()
  }

  // A possible duplicate: Duplicate (skipped) or Not a duplicate (added).
  markDuplicate(e) {
    const item = this._previewData?.items?.[Number(e.currentTarget.dataset.index)]
    if (!item) return
    item.keep = e.currentTarget.dataset.keep === "true"
    const row = e.currentTarget.closest("tr")
    row.querySelectorAll("[data-keep]").forEach((b) => {
      const on = (b.dataset.keep === "true") === item.keep
      b.className = `px-2 py-0.5 text-[13px] cursor-pointer ${on ? DUP_ON : DUP_OFF}`
      b.setAttribute("aria-pressed", on ? "true" : "false")
    })
    this._renderImportButton()
    this._renderReplaceNote()
  }

  // Every possible duplicate at once (Keane, K3-27 2026-10-08: 369 of them
  // to click one by one): all Not a duplicate (added) or all Duplicate.
  markAllDuplicates(e) {
    const keep = e.currentTarget.dataset.keep === "true"
    const possibles = (this._previewData?.items || []).filter((i) => i.status === "possible")
    if (!possibles.length) return
    possibles.forEach((i) => { i.keep = keep })
    this.previewTarget.querySelectorAll("button[data-keep][data-index]").forEach((b) => {
      const on = (b.dataset.keep === "true") === keep
      b.className = `px-2 py-0.5 text-[13px] cursor-pointer ${on ? DUP_ON : DUP_OFF}`
      b.setAttribute("aria-pressed", on ? "true" : "false")
    })
    this._renderImportButton()
    this._renderReplaceNote()
    track("Time List Duplicates Marked All", { kind: this.kind, keep, count: possibles.length })
  }

  // Add to the list (default) or replace the whole list (K8-28).
  setMode(e) {
    if (!this._previewData) return
    const replace = e.currentTarget.dataset.replace === "true"
    this._previewData.replace = replace
    this.previewTarget.querySelectorAll("[data-time-lists-mode] button").forEach((b) => {
      const on = (b.dataset.replace === "true") === replace
      b.className = `px-2 py-0.5 text-[13px] cursor-pointer ${on ? DUP_ON : DUP_OFF}`
      b.setAttribute("aria-pressed", on ? "true" : "false")
    })
    this._renderImportButton()
    this._renderReplaceNote()
    track("Time List Import Mode Changed", { kind: this.kind, replace })
  }

  // In replace mode the file's items already in the list count too: they're updated.
  _toAdd() {
    const replace = this._previewData?.replace
    return (this._previewData?.items || []).filter((i) => i.status === "new" || (i.status === "possible" && i.keep) || (replace && i.status === "exists"))
  }

  // What replacing would do: how many of the list's active items the file
  // doesn't have (archived), how many it has (updated), and the list items
  // a flagged duplicate was said to be (kept).
  _replacePlan() {
    const items = this._previewData?.items || []
    const inFile = new Set(this._toAdd().map((i) => String(i.key).toLowerCase()))
    const keepKeys = items.filter((i) => i.status === "possible" && !i.keep && i.match?.where === "list" && i.match?.key).map((i) => String(i.match.key))
    const keep = new Set(keepKeys.map((k) => k.toLowerCase()))
    const active = (this.lists[this.kind] || []).filter((i) => i.active)
    const archived = active.filter((i) => !inFile.has(String(i.key).toLowerCase()) && !keep.has(String(i.key).toLowerCase())).length
    const updated = items.filter((i) => i.status === "exists").length
    return { archived, updated, keepKeys }
  }

  _renderReplaceNote() {
    const note = this.previewTarget.querySelector("[data-time-lists-replace-note]")
    if (!note) return
    const replace = !!this._previewData?.replace
    note.classList.toggle("hidden", !replace)
    if (!replace) return
    const { archived, updated } = this._replacePlan()
    note.textContent = this.t("replace_note", { archived, updated })
  }

  confirmImport() {
    if (!this._toAdd().length || this._busy) return
    if (!this._previewData?.replace) return this._import()
    // Replacing archives rows: a proper confirmation first.
    const { archived, updated } = this._replacePlan()
    this.replaceTextTarget.textContent = this.t("replace_text", { archived, updated })
    this.replaceModalTarget.classList.remove("hidden")
  }

  closeReplace() {
    this.replaceModalTarget.classList.add("hidden")
  }

  replaceConfirmed() {
    this.closeReplace()
    this._import()
  }

  async _import() {
    const items = this._toAdd().map(({ key, name, description, job, job_name, extra }) => {
      const item = this.isPhases ? { job, job_name, key, name } : this.withDesc ? { key, name, description } : { key, name }
      if (extra) item.extra = extra // the sheet's rates, Interac numbers, types, units (K8-34)
      return item
    })
    if (!items.length || this._busy) return
    const replace = !!this._previewData?.replace
    const possibles = (this._previewData?.items || []).filter((i) => i.status === "possible")
    const kept = possibles.filter((i) => i.keep).length
    const body = { items, possible_kept: kept, possible_skipped: possibles.length - kept }
    if (replace) {
      body.replace = true
      body.keep_keys = this._replacePlan().keepKeys
    }
    this._busy = true
    const data = await this._request("POST", `${this.urlValue}/${this.kind}/import`, body)
    this._busy = false
    if (!data) return
    this._clearPreview()
    this._take(data)
    if (replace) showToast(this.t("replaced_toast", { count: data.created, updated: data.updated, archived: data.archived }))
    else if (this.isPhases && data.jobs_created) showToast(this.t("added_with_jobs", { count: data.created, jobs: data.jobs_created }))
    else showToast(data.skipped ? this.t("added_skipped", { count: data.created, skipped: data.skipped }) : this.t("added", { count: data.created }))
  }

  cancelImport() {
    this._clearPreview()
  }

  // ── Drawing ────────────────────────────────────────────────────────

  _take(data) {
    LISTS.forEach((k) => { if (data[k]) this.lists[k] = data[k] })
    if (data.settings) this.settings = data.settings
    if (typeof data.phases_total === "number") this.phasesTotal = data.phases_total
    if (data.phases && data.job_id) this.phases[Number(data.job_id)] = data.phases
    if (data.jobs_created !== undefined) this.phases = {} // a phases import: what's cached is stale
    this._renderTabs()
    this._renderList()
    if (this.isPhases) this._ensurePhases()
  }

  _renderTabs() {
    this.tabTargets.forEach((tab) => {
      const on = this.section ? tab.dataset.section === this.section : (!tab.dataset.section && tab.dataset.kind === this.kind)
      NAV_ON.forEach((c) => tab.classList.toggle(c, on))
      NAV_OFF.forEach((c) => tab.classList.toggle(c, !on))
      tab.setAttribute("aria-selected", on ? "true" : "false")
      const count = tab.querySelector("[data-time-lists-target='count']")
      if (count) {
        count.classList.toggle("text-brand", on)
        count.classList.toggle("text-gray-400", !on)
      }
    })
    this.countTargets.forEach((el) => {
      const n = el.dataset.kind === "phases" ? this.phasesTotal : (this.lists[el.dataset.kind] || []).filter((i) => i.active).length
      el.textContent = n ? String(n) : ""
      el.hidden = !n
    })
    if (this.hasPaneTitleTarget) this.paneTitleTarget.textContent = this.t(`tab_${this.kind}`)
    if (this.hasPaneHelpTarget) this.paneHelpTarget.textContent = this.t(`help_${this.kind}`)
    this.newKeyTarget.placeholder = this.t(`key_ph_${this.kind}`)
    this.newNameTarget.placeholder = this.t(`name_ph_${this.kind}`)
    this.newKeyTarget.setAttribute("aria-label", this.t(`key_${this.kind}`))
    this.newNameTarget.setAttribute("aria-label", this.t(this.withDesc ? "type" : this.isPhases ? "description" : "name"))
    this.newDescTarget.placeholder = this.t("desc_ph_equipment")
    this.newDescTarget.setAttribute("aria-label", this.t("description"))
    this.newDescTarget.classList.toggle("hidden", !this.withDesc)
    if (this.hasNewKeyLabelTarget) this.newKeyLabelTarget.textContent = this.t(`key_${this.kind}`)
    if (this.hasNewNameLabelTarget) this.newNameLabelTarget.textContent = this.t(this.withDesc ? "type" : this.isPhases ? "description" : "name")
    if (this.hasNewDescRowTarget) this.newDescRowTarget.hidden = !this.withDesc
    this._renderJobPicker()
    this._renderSettingsBox()
  }

  // ── Equipment's company switches (Phelipe 2026-10-09) ─────────────
  // Equipment hours, and taking machine hours out of labor: the same
  // settings the phone's Time Tracker Settings and Company Settings →
  // Timesheets change, at the top of the Equipment list.
  _renderSettingsBox() {
    if (!this.hasSettingsBoxTarget) return
    const show = this.kind === "equipment" && !this.section && !!this.settings && this.hasSettingsUrlValue
    this.settingsBoxTarget.hidden = !show
    if (!show) return
    const on = this.settings.equipment_enabled
    const box = (key, checked, disabled) => `
      <label class="flex items-start gap-2 ${disabled ? "opacity-50" : "cursor-pointer"}">
        <input type="checkbox" class="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-brand focus:ring-brand"
          data-key="${key}" data-action="change->time-lists#setSetting" ${checked ? "checked" : ""} ${disabled ? "disabled" : ""}>
        <span><span class="block text-[13px] font-semibold text-gray-900">${esc(this.t(`equipment_settings.${key}_label`))}</span>
        <span class="block text-[13px] text-gray-500">${esc(this.t(`equipment_settings.${key}_help`))}</span></span>
      </label>`
    this.settingsBoxTarget.innerHTML = `
      <div class="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 space-y-2">
        ${box("equipment_enabled", on, false)}
        ${box("split_equipment_from_labor", on && this.settings.split_equipment_from_labor, !on)}
      </div>`
  }

  async setSetting(e) {
    const input = e.currentTarget
    const key = input.dataset.key
    const on = input.checked
    input.disabled = true
    this._error(null)
    const data = await this._request("PATCH", this.settingsUrlValue, { settings: { [key]: on }, source: "codes_lists" })
    if (data?.settings) {
      this.settings = data.settings
      showToast(this.t("equipment_settings.saved"))
    }
    this._renderSettingsBox()
  }

  _renderList() {
    const words = this._words
    const hit = (i) => !words.length || words.every((w) => this._haystack(i).includes(w))
    const items = this.items
    const allActive = items.filter((i) => i.active)
    const active = allActive.filter(hit)
    const archived = items.filter((i) => !i.active && hit(i))
    if (this.hasSearchCountTarget) this.searchCountTarget.textContent = words.length ? this.t("search_count", { shown: active.length, total: allActive.length }) : ""
    let html = ""
    if (this.isPhases && !this.jobId) html += `<p class="text-[13px] text-gray-400 py-2">${esc(this.t("no_jobs_yet"))}</p>`
    else if (words.length && !active.length) html += `<p class="text-[13px] text-gray-400 py-2">${esc(this.t("search_none", { q: this.query.trim() }))}</p>`
    else if (!active.length) html += `<p class="text-[13px] text-gray-400 py-2">${esc(this.t(`empty_${this.kind}`))}</p>`
    else if (this.withDesc) html += `<div class="flex items-center gap-2 pb-1 text-[13px] font-medium text-gray-500 border-b border-gray-200"><span class="w-24">${esc(this.t("key_equipment"))}</span><span class="w-40">${esc(this.t("type"))}</span><span class="flex-1">${esc(this.t("description"))}</span></div>`
    else html += `<div class="flex items-center gap-2 pb-1 text-[13px] font-medium text-gray-500 border-b border-gray-200"><span class="w-36">${esc(this.t(`key_${this.kind}`))}</span><span class="flex-1">${esc(this.t(this.isPhases ? "description" : "name"))}</span></div>`
    html += active.map((i) => this._row(i)).join("")
    if (archived.length) {
      html += `<button type="button" class="mt-3 text-[13px] text-gray-500 hover:text-gray-700 underline cursor-pointer" data-act="toggleArchived" data-action="click->time-lists#rowAction">${esc(this.showArchived ? this.t("hide_archived") : this.t("show_archived", { count: archived.length }))}</button>`
      if (this.showArchived) html += archived.map((i) => this._row(i)).join("")
    }
    this.listTarget.innerHTML = html
  }

  _row(item) {
    const btn = (act, label, extra = "") => `<button type="button" class="px-2 py-0.5 rounded-lg text-[13px] cursor-pointer ${extra}" data-act="${act}" data-id="${item.id}" data-action="click->time-lists#rowAction">${esc(label)}</button>`
    const input = "px-2 py-1 border border-gray-300 rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
    const keyW = this.withDesc ? "w-24" : "w-36"
    const nameW = this.withDesc ? "w-40" : "flex-1"
    if (this.editing === item.id) {
      return `<div class="flex items-center gap-2 py-1.5 border-b border-gray-100" data-row="${item.id}">
        <input type="text" maxlength="60" value="${esc(item.key)}" data-edit-key data-action="keydown->time-lists#editKey" aria-label="${esc(this.t(`key_${this.kind}`))}" class="${keyW} ${input}">
        <input type="text" maxlength="120" value="${esc(item.name)}" data-edit-name data-action="keydown->time-lists#editKey" aria-label="${esc(this.t(this.withDesc ? "type" : this.isPhases ? "description" : "name"))}" class="${nameW} ${input}">
        ${this._extraInputs(item)}
        ${this.withDesc ? `<input type="text" maxlength="160" value="${esc(item.description)}" data-edit-desc data-action="keydown->time-lists#editKey" aria-label="${esc(this.t("description"))}" placeholder="${esc(this.t("desc_ph_equipment"))}" class="flex-1 ${input}">` : ""}
        ${btn("save", this.t("save"), "font-semibold text-white bg-brand hover:bg-brand-dark")}
        ${btn("cancel", this.t("cancel"), "text-gray-600 hover:bg-gray-100")}
      </div>`
    }
    const actions = item.active
      ? btn("edit", this.t("edit"), "text-gray-600 hover:bg-gray-100") + btn("archive", this.t("archive"), "text-gray-600 hover:bg-gray-100")
      : `<span class="text-[13px] text-gray-400">${esc(this.t("archived"))}</span>` + btn("restore", this.t("restore"), "text-brand hover:bg-gray-100")
    // A job says how many phases it holds.
    const extra = this.kind === "jobs" && item.phases_count ? ` <span class="text-gray-400">· ${item.phases_count} ${esc(this.t("phases_word"))}</span>` : ""
    return `<div class="flex items-center gap-2 py-1.5 border-b border-gray-100 ${item.active ? "" : "opacity-70"}" data-row="${item.id}">
      <span class="${keyW} text-[13px] font-medium text-gray-900 truncate" title="${esc(item.key)}">${esc(item.key)}</span>
      <span class="${nameW} text-[13px] text-gray-600 truncate" title="${esc(item.name)}">${esc(item.name)}${extra}</span>
      ${this.withDesc ? `<span class="flex-1 text-[13px] text-gray-500 truncate" title="${esc(item.description)}">${esc(item.description)}</span>` : ""}
      ${this._extraText(item)}
      ${actions}
    </div>`
  }

  // The foreman's report's fields on a row (K8-34): shown beside the name,
  // edited with the row. A unit's type is a pick from the Equipment types list.
  _extraText(item) {
    const fields = EXTRA[this.kind] || []
    const parts = fields.map((f) => {
      let value = item[f.field]
      if (f.select) value = (this.lists[f.select] || []).find((t) => t.id === value)?.label
      if (value === null || value === undefined || value === "") return ""
      return `<span class="text-gray-400">${esc(this.t(`field_${f.field}`))}:</span> ${esc(f.field.startsWith("rate") ? this._money(value) : value)}`
    }).filter(Boolean)
    return parts.length ? `<span class="hidden md:inline text-[13px] text-gray-500 truncate">${parts.join(" · ")}</span>` : ""
  }

  _extraInputs(item) {
    const input = "px-2 py-1 border border-gray-300 rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
    return (EXTRA[this.kind] || []).map((f) => {
      const label = esc(this.t(`field_${f.field}`))
      if (f.select) {
        const options = (this.lists[f.select] || []).filter((t) => t.active || t.id === item[f.field])
          .map((t) => `<option value="${t.id}" ${t.id === item[f.field] ? "selected" : ""}>${esc(t.label)}</option>`).join("")
        return `<select data-edit-field="${f.field}" aria-label="${label}" class="w-40 ${input}"><option value="">${esc(this.t("no_type"))}</option>${options}</select>`
      }
      const value = item[f.field] === null || item[f.field] === undefined ? "" : item[f.field]
      return `<input type="text" maxlength="40" value="${esc(value)}" data-edit-field="${f.field}" data-action="keydown->time-lists#editKey" aria-label="${label}" placeholder="${label}" class="w-24 ${input}">`
    }).join("")
  }

  _money(value) {
    const n = Number(value)
    return Number.isFinite(n) ? `$${n.toFixed(2)}` : String(value)
  }

  // "Rate $/h $178.49 · With operator $/h $254.13 · Category 7": the sheet's
  // extra columns, so the office sees what comes in with each item (K8-34).
  _extraLine(extra) {
    const label = (k) => this.t(k === "type_code" ? "field_type_id" : `field_${k}`)
    return Object.entries(extra).map(([k, v]) => `${label(k)} ${k === "rate" || k === "rate_with_labor" ? this._money(v) : v}`).join(" · ")
  }

  _renderPreview() {
    const { items = [], source } = this._previewData || {}
    items.forEach((item, index) => { item.index = index; if (item.status === "possible" && item.keep === undefined) item.keep = false })
    const shown = [...items].sort((a, b) => (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9) || a.index - b.index)
    const possibles = items.filter((i) => i.status === "possible").length
    const rows = shown.map((i) => {
      let decide = ""
      if (i.status === "possible") {
        const like = [i.match?.key, i.match?.name].filter(Boolean).join(" · ")
        const where = this.t(i.match?.where === "file" ? "in_file" : "in_list")
        const pill = (keep, label) => `<button type="button" class="px-2 py-0.5 text-[13px] cursor-pointer ${keep === i.keep ? DUP_ON : DUP_OFF}" aria-pressed="${keep === i.keep}" data-keep="${keep}" data-index="${i.index}" data-action="click->time-lists#markDuplicate">${esc(label)}</button>`
        decide = `<div class="mt-0.5 text-[13px] text-amber-800">${esc(this.t("looks_like", { item: like }))} (${esc(where)})</div>
          <div class="mt-1 inline-flex rounded-md border border-gray-300 overflow-hidden">${pill(false, this.t("dup_yes"))}${pill(true, this.t("dup_no"))}</div>`
      }
      // A phase row names its job too: "10 / 40.1 · Labor (Paul Ready Mix)".
      const key = this.isPhases ? `${esc(i.job)} / ${esc(i.key)}` : esc(i.key)
      const name = this.isPhases ? `${esc(i.name)}${i.job_name ? ` <span class="text-gray-400">${esc(i.job_name)}</span>` : ""}` : esc(i.name)
      return `<tr class="border-t border-purple-100 align-top ${i.status === "possible" ? "bg-amber-50" : ""}" data-status="${i.status}">
        <td class="px-3 py-1.5 font-medium text-gray-900 whitespace-nowrap">${key || "—"}</td>
        <td class="px-3 py-1.5 text-gray-600">${name}${i.extra ? `<div class="mt-0.5 text-[13px] text-gray-400">${esc(this._extraLine(i.extra))}</div>` : ""}${decide}</td>
        ${this.withDesc ? `<td class="px-3 py-1.5 text-gray-500">${esc(i.description)}</td>` : ""}
        <td class="px-3 py-1.5 text-right whitespace-nowrap"><span class="inline-block px-2 py-0.5 rounded-full ${CHIPS[i.status] || CHIPS.invalid}">${esc(this.t(`status_${i.status}`))}</span></td>
      </tr>`
    }).join("")
    // Replacing a whole list is for the phone's lists; phases only ever add.
    const canReplace = !this.isPhases && (this.lists[this.kind] || []).some((i) => i.active)
    this.previewTarget.innerHTML = `
      <div class="px-4 py-3 bg-purple-50 rounded-t-xl border-b border-purple-200">
        <p class="text-[13px] font-semibold text-gray-900">${esc(this.t("preview_title", { count: items.length }))}</p>
        <p class="text-[13px] text-purple-700">${esc(this.t(source === "ai" ? "by_ai" : this.isPhases ? "by_headers" : "by_rules"))}</p>
        ${canReplace ? `<div class="mt-1.5 inline-flex rounded-md border border-gray-300 overflow-hidden bg-white" data-time-lists-mode role="group">
          <button type="button" class="px-2 py-0.5 text-[13px] cursor-pointer ${DUP_ON}" aria-pressed="true" data-replace="false" data-action="click->time-lists#setMode">${esc(this.t("mode_add"))}</button>
          <button type="button" class="px-2 py-0.5 text-[13px] cursor-pointer ${DUP_OFF}" aria-pressed="false" data-replace="true" data-action="click->time-lists#setMode">${esc(this.t("mode_replace"))}</button>
        </div>
        <p class="mt-1 text-[13px] text-red-700 hidden" data-time-lists-replace-note></p>` : ""}
        ${possibles ? `<p class="mt-1 text-[13px] text-amber-800">${esc(this.t("possible_note", { count: possibles }))}</p>
        <div class="mt-1.5 flex flex-wrap gap-2">
          <button type="button" class="px-2 py-0.5 rounded-md border border-gray-300 bg-white text-[13px] text-gray-700 hover:bg-gray-50 cursor-pointer" data-keep="true" data-action="click->time-lists#markAllDuplicates">${esc(this.t("all_not_duplicates"))}</button>
          <button type="button" class="px-2 py-0.5 rounded-md border border-gray-300 bg-white text-[13px] text-gray-700 hover:bg-gray-50 cursor-pointer" data-keep="false" data-action="click->time-lists#markAllDuplicates">${esc(this.t("all_duplicates"))}</button>
        </div>` : ""}
      </div>
      <div class="max-h-64 overflow-y-auto"><table class="w-full text-[13px]"><tbody>${rows}</tbody></table></div>
      <div class="flex justify-end gap-2 px-4 py-3 border-t border-purple-200">
        <button type="button" class="px-3 py-1.5 rounded-lg text-[13px] text-gray-700 border border-gray-300 hover:bg-gray-50 cursor-pointer" data-action="click->time-lists#cancelImport">${esc(this.t("cancel"))}</button>
        <button type="button" class="px-3 py-1.5 rounded-lg text-[13px] font-semibold text-white bg-purple-600 hover:bg-purple-700 cursor-pointer disabled:opacity-50" data-time-lists-import data-action="click->time-lists#confirmImport"></button>
      </div>`
    this._renderImportButton()
    this.previewTarget.classList.remove("hidden")
  }

  _renderImportButton() {
    const button = this.previewTarget.querySelector("[data-time-lists-import]")
    if (!button) return
    const count = this._toAdd().length
    const replace = !!this._previewData?.replace
    button.textContent = count ? this.t(replace ? "replace_n" : "add_n", { count }) : this.t("nothing_new")
    button.disabled = !count
    button.classList.toggle("bg-purple-600", !replace)
    button.classList.toggle("hover:bg-purple-700", !replace)
    button.classList.toggle("bg-red-600", replace)
    button.classList.toggle("hover:bg-red-700", replace)
  }

  _clearPreview() {
    this._previewData = null
    if (!this.hasPreviewTarget) return
    this.previewTarget.innerHTML = ""
    this.previewTarget.classList.add("hidden")
  }

  // While the Add pop-up is open its own line shows the error, where the eye is.
  _error(message) {
    const el = this._addOpen && this.hasAddErrorTarget ? this.addErrorTarget : this.hasErrorTarget ? this.errorTarget : null
    if (!el) return
    el.textContent = message || ""
    el.classList.toggle("hidden", !message)
  }

  async _request(method, url, body) {
    const headers = { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "", "Accept": "application/json" }
    let payload = body
    if (body && !(body instanceof FormData)) {
      headers["Content-Type"] = "application/json"
      payload = JSON.stringify(body)
    }
    try {
      const res = await fetch(url, { method, headers, body: payload, credentials: "same-origin" })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        this._error(data.error || this.t("errors.generic"))
        return null
      }
      return data
    } catch (_) {
      this._error(this.t("errors.generic"))
      return null
    }
  }
}
